import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { colors, generateShadows } from '../theme/colors';

/**
 * Generates responsive font sizes based on Material-UI breakpoints
 * @param {Object} theme - Material-UI theme object
 * @param {number} defaultSize - Default font size multiplier
 * @param {number} xl - Font size for xl breakpoint and below
 * @param {number} lg - Font size for lg breakpoint and below
 * @param {number} md - Font size for md breakpoint and below
 * @param {number} sm - Font size for sm breakpoint and below
 * @returns {Object} Responsive font size configuration
 */
const getResponsiveFontSize = (theme, defaultSize, xl, lg, md, sm) => ({
  fontSize: theme.spacing(defaultSize),

  [theme.breakpoints.down('xl')]: {
    fontSize: theme.spacing(xl),
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: theme.spacing(lg),
  },
  [theme.breakpoints.down('md')]: {
    fontSize: theme.spacing(md),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: theme.spacing(sm),
  },
});

/**
 * Generates typography variants for the theme
 * @param {Object} theme - Material-UI theme object
 * @returns {Object} Typography configuration with responsive font sizes
 */
const getTypography = (theme) => ({
  h1: {
    ...getResponsiveFontSize(theme, 14, 13, 11, 11, 10),

    fontWeight: 600,
    lineHeight: theme.spacing(18),
  },
  h2: {
    ...getResponsiveFontSize(theme, 12, 11, 11, 11, 10),
    fontSize: theme.spacing(12),
    fontWeight: 600,
    lineHeight: theme.spacing(16),
  },
  h3: {
    ...getResponsiveFontSize(theme, 6.5, 6, 5.5, 5, 4.5),
    fontWeight: 600,
    lineHeight: theme.spacing(9),
  },
  h4: {
    ...getResponsiveFontSize(theme, 6, 5, 4.75, 4.75, 4),
    fontWeight: 400,
    lineHeight: theme.spacing(8),
  },
  h5: {
    ...getResponsiveFontSize(theme, 5, 4.5, 4, 4, 4),
    fontWeight: 500,
    lineHeight: theme.spacing(7),
  },
  h6: {
    ...getResponsiveFontSize(theme, 5, 4.5, 4, 4, 3.5),

    fontWeight: 400,
    lineHeight: theme.spacing(5),
  },
  body1: {
    ...getResponsiveFontSize(theme, 4, 3.75, 3.75, 3.75, 3.5),
    fontWeight: 400,
    lineHeight: theme.spacing(6),
  },
  body2: {
    ...getResponsiveFontSize(theme, 3.5, 3.25, 3.25, 3.25, 3.25),
    fontSize: theme.spacing(3.5),
    fontWeight: 400,
    lineHeight: theme.spacing(5.5),
  },
  bold1: {
    ...getResponsiveFontSize(theme, 4, 3.75, 3.5, 3.5, 3),
    fontWeight: 600,
    lineHeight: theme.spacing(6),
  },
  bold2: {
    ...getResponsiveFontSize(theme, 4, 3.75, 3.5, 3.5, 3),
    fontWeight: 500,
    lineHeight: theme.spacing(6),
  },
  bold3: {
    ...getResponsiveFontSize(theme, 3.5, 3.25, 3.25, 3, 3),
    fontWeight: 600,
    lineHeight: theme.spacing(6),
  },
  subtitle1: {
    fontSize: theme.spacing(3),
    fontWeight: 600,
    lineHeight: theme.spacing(4),
  },
  caption1: {
    fontSize: theme.spacing(3),
    fontWeight: 400,
    lineHeight: theme.spacing(4),
  },
  overline1: {
    fontSize: theme.spacing(2.5),
    fontWeight: 400,
    lineHeight: theme.spacing(4),
  },
});

/**
 * Creates base theme configuration with language-specific settings
 * @param {string} language - Language code (e.g., 'ar' for Arabic, 'en' for English)
 * @returns {Object} Base theme configuration
 */
const getBaseTheme = (language) => {
  const baseTheme = createTheme({
    spacing: (factor) => `${0.25 * factor}rem`,
  });

  return {
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            direction: language === 'ar' ? 'rtl' : 'ltr',
          },
        },
      },
      MuiDrawer: {
        defaultProps: {
          anchor: language === 'ar' ? 'right' : 'left',
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            // Card padding
            padding: baseTheme.spacing(4),
            [baseTheme.breakpoints.down('sm')]: {
              padding: baseTheme.spacing(3),
            },
          },
        },
      },
    },
    typography: {
      fontFamily: "'Poppins' , 'Noto Naskh Arabic', sans-serif",
      ...getTypography(baseTheme),
    },
    direction: language === 'ar' ? 'rtl' : 'ltr',
    spacing: baseTheme.spacing,
  };
};

/**
 * LightThemeWrapper component that forces light theme for wrapped components
 * This component ensures that children always use light theme colors regardless
 * of the global theme setting, useful for components that should never follow dark mode
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with light theme
 * @returns {JSX.Element} Theme provider with light theme only
 */
const LightThemeWrapper = ({ children }) => {
  const { i18n } = useTranslation();

  const lightTheme = createTheme({
    ...getBaseTheme(i18n.language),
    shadows: generateShadows(
      colors.lightShadowColor,
      colors.lightShadowColor14,
      colors.lightShadowColor12
    ),
    palette: {
      mode: 'light',
      background: {
        default: colors.lightBackground,
        menuSelected: colors.menuSelectedLight,
      },
      border: {
        main: colors.borderMainLight,
      },
      text: {
        success: colors.textSuccess,
        danger: colors.textDanger,
        warning: colors.textWarning,
      },
      error: {
        main: colors.errorLight.main,
        ...colors.error,
      },
      success: colors.success,
      primary: colors.primaryLight,
      secondary: colors.secondaryLight,
      warning: {
        main: colors.warning.main,
        Warning_95: colors.warning.Warning_95,
        Warning_30: colors.warning.Warning_30,
      },
    },
  });

  return <ThemeProvider theme={lightTheme}>{children}</ThemeProvider>;
};

LightThemeWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

export default LightThemeWrapper;
